/**
 * The evenings that already happened.
 *
 * A game night used to vanish the day after it ran, taking with it the one
 * thing worth keeping: that it worked. These stay for a fortnight, with who
 * came and a button to run the same night again - which is how most
 * recurring plans actually start, rather than from a blank form.
 *
 * Beside it, the panel a new member sees: what this crew plays, when it is
 * around, and what is next. A list of usernames tells somebody nothing.
 */

import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, serverUrl } from '@/lib/api';
import { absolute } from './user';
import { Lightbox } from './Lightbox';
import { useToast } from './ui';
import type { PublicUser } from '@/lib/types';
import { cn } from '@/lib/cn';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { GameMark } from './GameMark';
import { Button } from './ui';
import type { Crew } from './Crew';

export type Highlight = {
  id: string;
  fileId: string;
  url: string;
  type: string;
  name: string;
  by: string;
  at: number;
  note: string;
  user?: PublicUser | null;
};

export type PastEvent = {
  id: string;
  title: string;
  description: string;
  gameId: string | null;
  startsAt: number;
  endsAt: number;
  durationMin: number;
  repeat: 'weekly' | 'fortnightly' | null;
  went: number;
  people: PublicUser[];
  highlights?: Highlight[];
};

export function CrewHistory({ crew, canManage, onAgain }: {
  crew: Crew;
  canManage: boolean;
  onAgain: (event: PastEvent) => void;
}) {
  const [open, setOpen] = useState(false);
  const past = useQuery({
    queryKey: ['crew-past', crew.id],
    queryFn: () => api.get<{ events: PastEvent[] }>('/api/crews/' + crew.id + '/events/past'),
    enabled: open,
    staleTime: 60_000
  });

  const list = past.data?.events ?? [];

  return (
    <section className="pugo-panel rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2.5 text-left outline-none"
      >
        <Icon name="history" size={18} className="flex-none text-ink" />
        <h3 className="m-0 flex-1 text-[16px] font-bold text-ink">Game nights you have had</h3>
        <Icon name="chevronD" size={15} className={cn('flex-none text-ghost transition-transform', open && 'rotate-180')} />
      </button>

      {open ? (
        past.isPending ? (
          <div className="mt-3 flex flex-col gap-2">
            {[0, 1].map((i) => <div key={i} className="h-[58px] animate-pulse rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2" />)}
          </div>
        ) : !list.length ? (
          <p className="m-0 mt-3 text-[13.5px] text-mute">Nothing yet. The ones you run are kept here for a fortnight.</p>
        ) : (
          <ul className="m-0 mt-3 flex list-none flex-col gap-1.5 p-0">
            {list.map((e) => (
              <li key={e.id} className="flex flex-wrap items-center gap-2.5 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-2.5">
                {e.gameId ? <GameMark id={e.gameId} size={26} plain /> : <Icon name="calendar" size={20} className="text-mute" />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13.5px] font-semibold text-ink">{e.title}</span>
                  <span className="block truncate text-[12px] text-mute">
                    {new Date(e.startsAt).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })}
                    {' · '}{e.went} {e.went === 1 ? 'person went' : 'people went'}
                  </span>
                </span>
                <span className="flex flex-none items-center">
                  {e.people.slice(0, 4).map((u, i) => (
                    <span key={u.id} className="rounded-full ring-2 ring-[var(--bg-2)]" style={{ marginLeft: i ? -8 : 0 }}>
                      <Avatar user={u} size={22} />
                    </span>
                  ))}
                </span>
                {canManage ? (
                  <Button size="sm" kind="outline" onClick={() => onAgain(e)}>Plan this again</Button>
                ) : null}

                {/* What happened on the night, kept with the night. */}
                <Highlights crew={crew} event={e} />
              </li>
            ))}
          </ul>
        )
      ) : null}
    </section>
  );
}

/**
 * Clips and screenshots from an evening.
 *
 * Attached to the event rather than to a feed, because the reason to keep a
 * clip is that it belongs to a night everybody remembers. Yours are yours to
 * take down; an officer can remove any of them.
 */
function Highlights({ crew, event }: { crew: Crew; event: PastEvent }) {
  const me = useSession((s) => s.me);
  const queryClient = useQueryClient();
  const toast = useToast();
  const file = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<Highlight[]>(event.highlights ?? []);
  const [busy, setBusy] = useState(false);
  const [look, setLook] = useState<Highlight | null>(null);

  const canManage = crew.myRole === 'owner' || crew.myRole === 'officer';

  async function add(f: File) {
    setBusy(true);
    try {
      /* The same upload every attachment uses, then a line on the event. */
      const res = await fetch(serverUrl() + '/api/files', {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + (localStorage.getItem('pogu.token') ?? ''),
          'Content-Type': f.type || 'application/octet-stream',
          'X-File-Type': f.type || 'application/octet-stream',
          'X-File-Name': encodeURIComponent(f.name),
          'X-File-Kind': 'attachment'
        },
        body: f
      });
      if (!res.ok) throw new Error('That file would not upload.');
      const up = await res.json();
      const out = await api.post<{ highlights: Highlight[] }>(
        '/api/crews/' + crew.id + '/events/' + event.id + '/highlights',
        { fileId: up.id ?? up.file?.id ?? up.attachment?.id }
      );
      setRows(out.highlights);
      void queryClient.invalidateQueries({ queryKey: ['crew-past', crew.id] });
      toast('Kept with that night', 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
      if (file.current) file.current.value = '';
    }
  }

  async function remove(id: string) {
    try {
      const out = await api.del<{ highlights: Highlight[] }>('/api/crews/' + crew.id + '/events/' + event.id + '/highlights/' + id);
      setRows(out.highlights);
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  return (
    <div className="mt-2 w-full">
      {rows.length ? (
        <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
          {rows.map((h) => (
            <li key={h.id} className="relative">
              <button
                type="button"
                onClick={() => setLook(h)}
                className="block h-[64px] w-[96px] overflow-hidden rounded-[calc(10px*var(--radius-mul,1))] bg-surface-3 outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]"
              >
                {h.type.startsWith('image/')
                  ? <img src={absolute(h.url)} alt={h.note || h.name} className="h-full w-full object-cover" />
                  : <span className="grid h-full place-items-center text-mute"><Icon name="video" size={20} /></span>}
              </button>
              {h.by === me?.id || canManage ? (
                <button
                  type="button"
                  aria-label="Remove this highlight"
                  onClick={() => void remove(h.id)}
                  className="absolute top-1 right-1 grid h-5 w-5 place-items-center rounded-full bg-[rgba(0,0,0,.6)] text-white/80 transition-colors hover:text-white"
                >
                  <Icon name="close" size={10} />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      <button
        type="button"
        disabled={busy}
        onClick={() => file.current?.click()}
        className="mt-1.5 flex items-center gap-1.5 text-[12px] font-semibold text-mute outline-none transition-colors hover:text-accent"
      >
        <Icon name="image" size={13} />
        {busy ? 'Adding…' : rows.length ? 'Add another' : 'Add a clip or a screenshot'}
      </button>
      <input
        ref={file}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void add(f); }}
      />

      {look ? (
        <Lightbox
          /* The viewer takes attachments, which is what a highlight nearly is. */
          list={rows.filter((h) => h.type.startsWith('image/')).map((h) => ({
            id: h.fileId,
            url: h.url,
            name: h.note || h.name,
            type: h.type,
            size: 0,
            image: true
          }))}
          index={Math.max(0, rows.filter((h) => h.type.startsWith('image/')).findIndex((h) => h.id === look.id))}
          onClose={() => setLook(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * What a new member needs in the first ten seconds.
 *
 * Shown to everybody, because "what does this crew actually do" is a question
 * people who have been in it for a month still cannot answer. Built from what
 * the crew has: the games its members list, the hours they are actually
 * around, and the next thing on the calendar.
 */
export function CrewWelcome({ crew }: { crew: Crew }) {
  const catalog = useSession((s) => s.catalog);
  const me = useSession((s) => s.me);

  /* The games this crew plays, counted off its members' profiles. */
  const counts = new Map<string, number>();
  for (const m of crew.members ?? []) {
    for (const g of m.user.games ?? []) counts.set(g.id, (counts.get(g.id) ?? 0) + 1);
  }
  const games = [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([id, n]) => ({ id, n, name: catalog?.games.find((g) => g.id === id)?.name ?? id }));

  const next = (crew.events ?? [])[0] ?? null;
  const online = (crew.members ?? []).filter((m) => m.user.status && m.user.status !== 'offline').length;
  const mine = crew.myRole;

  if (!games.length && !next && !crew.description) return null;

  return (
    <section className="pugo-panel rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <div className="mb-2.5 flex items-center gap-2.5">
        <Icon name="handshake" size={18} className="text-ink" />
        <h3 className="m-0 flex-1 text-[16px] font-bold text-ink">
          {mine ? 'About ' + crew.name : 'What this crew is'}
        </h3>
      </div>

      {crew.description ? <p className="m-0 mb-3 text-[13.5px] leading-relaxed text-dim">{crew.description}</p> : null}

      <dl className="m-0 grid gap-2.5">
        {games.length ? (
          <div>
            <dt className="m-0 text-[11.5px] font-bold tracking-[.08em] text-mute uppercase">Usually plays</dt>
            <dd className="m-0 mt-1.5 flex flex-wrap gap-1.5">
              {games.map((g) => (
                <span key={g.id} className="flex items-center gap-1.5 rounded-full bg-surface-2 py-1 pr-3 pl-1 text-[12.5px] font-semibold text-dim">
                  <GameMark id={g.id} size={18} plain />
                  {g.name}
                  <span className="text-ghost">{g.n}</span>
                </span>
              ))}
            </dd>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <dt className="m-0 text-[11.5px] font-bold tracking-[.08em] text-mute uppercase">Around now</dt>
            <dd className="m-0 mt-0.5 text-[14px] font-bold text-ink">{online} of {crew.memberCount}</dd>
          </div>
          <div>
            <dt className="m-0 text-[11.5px] font-bold tracking-[.08em] text-mute uppercase">Next game night</dt>
            <dd className="m-0 mt-0.5 truncate text-[14px] font-bold text-ink">
              {next
                ? new Date(next.startsAt).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })
                : 'Nothing planned'}
            </dd>
          </div>
        </div>
      </dl>

      {!mine && me ? (
        <p className="mt-3 mb-0 text-[12.5px] text-mute">Ask to join and you will see its chat, its voice lounge and its calendar.</p>
      ) : null}
    </section>
  );
}
